import { initContract } from '@ts-rest/core'
import z from 'zod'
import { ErrorResponseSchema } from '../../shared'
import { GetLegacyHistoricalDataRequestSchema, GetLegacyHistoricalDataResponseSchema, GetLegacyToggleOrdersQuerySchema, GetLegacyToggleOrdersResponseSchema, GetLegacyUserDataBodySchema, GetLegacyUserDataResponseSchema, GetLegacyUserEmailsByIdsBodySchema, GetLegacyUserEmailsByIdsResponseSchema, GetLegacyUserEmailsQuerySchema, GetLegacyUserEmailsResponseSchema, GetLegacyUserEventRequestSchema, GetLegacyUserEventResponseSchema, GetReferCouponCountResponseSchema, GetReferCouponPageQuerySchema, GetReferCouponPageResponseSchema, GetReferCouponsByIdsBodySchema, GetReferCouponsByIdsResponseSchema, GetReferCouponUserCountResponseSchema, GetReferCouponUserPageQuerySchema, GetReferCouponUserPageResponseSchema, SearchLegacySubscriptionsByIdsBodySchema, SearchLegacySubscriptionsByIdsResponseSchema } from './schemas'

export const ContractOptions = {
  commonResponses: {
    400: ErrorResponseSchema,
    404: ErrorResponseSchema,
    500: ErrorResponseSchema,
  },
}

const c = initContract()

export const laravelDataExporterContract = c.router({
  getHealth: {
    method: 'GET',
    path: '/health',
    summary: 'Health check endpoint',
    description: 'Checks if the service is healthy',
    responses: {
      200: z.object({
        status: z.string(),
      }),
    },
  },
  getLegacyUserEmails: {
    method: 'GET',
    path: '/legacy-user-emails',
    summary: 'Get user emails with migration information from the legacy system',
    description: 'Get a list of user emails with migration information from the legacy system',
    query: GetLegacyUserEmailsQuerySchema,
    responses: {
      200: GetLegacyUserEmailsResponseSchema,
    },
  },
  getLegacyUserEmailsByIds: {
    method: 'POST',
    path: '/legacy-user-emails/by-ids',
    summary: 'Get legacy user emails by their Laravel user IDs',
    description: 'Resolve a list of Laravel user IDs to their emails and migration status. Used by bulk migration to drive a run from a user-ID list.',
    body: GetLegacyUserEmailsByIdsBodySchema,
    responses: {
      200: GetLegacyUserEmailsByIdsResponseSchema,
    },
  },
  getLegacyUserData: {
    method: 'POST',
    path: '/get-legacy-user-data',
    summary: 'Get user data for migration',
    description: 'Get a user data from the legacy system to the new system eg. User, Dog, Subscription, Order, Payment, Discount, etc...',
    body: GetLegacyUserDataBodySchema,
    responses: {
      200: GetLegacyUserDataResponseSchema,
    },
  },
  getLegacyUserEvents: {
    method: 'GET',
    path: '/legacy-user-events',
    summary: 'Get user events for a customer',
    description: 'Get the legacy user events for a customer mapped to a partial Phoenix user event',
    query: GetLegacyUserEventRequestSchema,
    responses: {
      200: GetLegacyUserEventResponseSchema,
    },
  },
  getLegacyToggleOrders: {
    method: 'GET',
    path: '/legacy-toggle-orders',
    summary: 'Get legacy orders with phoenix fulfilment toggle enabled',
    description: 'Queries the Laravel DB for orders where phoenix_fulfilment_toggle = true, filtered by make_date range and facility IDs. Used for reconciliation with Phoenix.',
    query: GetLegacyToggleOrdersQuerySchema,
    responses: {
      200: GetLegacyToggleOrdersResponseSchema,
    },
  },
  getLegacyHistoricalData: {
    method: 'GET',
    path: '/legacy-historical-data',
    summary: 'Get historical data for a customer',
    description: 'Get the historical data for a customer',
    query: GetLegacyHistoricalDataRequestSchema,
    responses: {
      200: GetLegacyHistoricalDataResponseSchema,
    },
  },
  getReferCouponCount: {
    method: 'GET',
    path: '/refer-coupons/count',
    summary: 'Get total count of refer coupons and unique discount percentages',
    responses: {
      200: GetReferCouponCountResponseSchema,
    },
  },
  getReferCoupons: {
    method: 'GET',
    path: '/refer-coupons',
    summary: 'Get paginated refer coupons using cursor-based pagination',
    query: GetReferCouponPageQuerySchema,
    responses: {
      200: GetReferCouponPageResponseSchema,
    },
  },
  getReferCouponsByIds: {
    method: 'POST',
    path: '/refer-coupons/by-ids',
    summary: 'Get specific refer coupons by their Laravel IDs for retry',
    body: GetReferCouponsByIdsBodySchema,
    responses: {
      200: GetReferCouponsByIdsResponseSchema,
    },
  },
  getReferCouponUserCount: {
    method: 'GET',
    path: '/refer-coupon-users/count',
    summary: 'Get total count of refer coupon users',
    responses: {
      200: GetReferCouponUserCountResponseSchema,
    },
  },
  getReferCouponUsers: {
    method: 'GET',
    path: '/refer-coupon-users',
    summary: 'Get paginated refer coupon users using cursor-based pagination',
    query: GetReferCouponUserPageQuerySchema,
    responses: {
      200: GetReferCouponUserPageResponseSchema,
    },
  },
  searchLegacySubscriptionsByIds: {
    method: 'POST',
    path: '/legacy-subscriptions/search',
    summary: 'Get subscription cadence data by legacy Laravel subscription IDs',
    body: SearchLegacySubscriptionsByIdsBodySchema,
    responses: {
      200: SearchLegacySubscriptionsByIdsResponseSchema,
    },
  },
}, ContractOptions)

export type LaravelDataExporterContract = typeof laravelDataExporterContract

export default laravelDataExporterContract
